'use client';

import React from 'react';

import { IconDepopIcon } from '@depop/web-ui-kit/Icons/IconDepopIcon';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import { MegaNav } from '@/components/MegaNav';
import { MobileNavigation } from '@/components/MobileNavigation';
import { Navigation } from '@/components/Navigation/';
import { SearchBarDesktop } from '@/components/SearchBar/SearchBarDesktop';
import {
  ActivityTrackerEventType,
  NavCtaActionTypes,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useShowHomepageSearchBar } from '@/modules/home/hooks/useShowHomepageSearchBar';
import { useProductsSearchBar } from '@/modules/product/hooks/useProductsSearchBar';
import { useHideFooter } from '@/modules/sharedLayout/useHideFooter';
import { useHideHeader } from '@/modules/sharedLayout/useHideHeader';
import { useHideMegaNav } from '@/modules/sharedLayout/useHideMegaNav';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function Header({
  isInAppView,
  isMobileDevice,
}: {
  isInAppView?: boolean;
  isMobileDevice?: boolean;
}) {
  const t = useTranslations('common');
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const pathname = usePathname();
  const isHeaderHidden = useHideHeader(
    Boolean(isInAppView),
    Boolean(isMobileDevice)
  );
  const isFooterHidden = useHideFooter(Boolean(isInAppView));
  const isMegaNavHidden = useHideMegaNav(Boolean(isInAppView));
  const { visibleSearchBar: isHomepageSearchBarVisible } =
    useShowHomepageSearchBar();
  const { isVisible: isProductSearchBarVisible } = useProductsSearchBar();

  const hideBorderBottom =
    isHomepageSearchBarVisible || isProductSearchBarVisible;
  Boolean(pathname.match(/^\/(?:([a-z]{2})\/)?sell/));

  const SKIP_LINKS = [
    { href: '#main', text: t('SkipToContent.main'), hideOnMobile: false },
    ...(isFooterHidden
      ? []
      : [
          {
            href: '#footer',
            text: t('SkipToContent.footer'),
            hideOnMobile: true,
          },
        ]),
  ];

  function handleLogoClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.CTA_ACTION, {
      type: NavCtaActionTypes.DEPOP_LOGO_CTA,
    });
  }

  if (isHeaderHidden) {
    return null;
  }

  return (
    <header className={styles.headerWrapper} role="banner" id="globalHeader">
      <div
        className={clsx(styles.headerContainer, {
          [styles['headerContainer--noBorderBottom']]: hideBorderBottom,
        })}
        // eslint-disable-next-line no-restricted-syntax
        data-testid="header"
      >
        <MobileNavigation />
        <div className={styles.skipLinkContainer}>
          {SKIP_LINKS.map(({ href, text, hideOnMobile }) => (
            <a
              className={clsx(styles.skipToLink, {
                [styles.hideSkipLink]: hideOnMobile,
              })}
              href={href}
              key={href}
            >
              {text}
            </a>
          ))}
        </div>
        <ExtendedLink
          href="/"
          onClick={handleLogoClick}
          aria-label="Depop homepage"
        >
          <IconDepopIcon className={styles.logo} aria-hidden="true" />
        </ExtendedLink>
        <SearchBarDesktop />
        <Navigation />
      </div>
      {!isMegaNavHidden && <MegaNav />}
    </header>
  );
}
